import React from "react";
import { HiOutlineTicket } from "react-icons/hi";
import { BiInfoCircle } from "react-icons/bi";
import { Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import useDesktopSize from "../../../../../../utils/hooks/useDesktopSize";
import EAQuantitySelector from "../../../../shared/QuantitySelector/EAQuantitySelector";
import { Assort } from "../../../../../models/Assort";
import { TicketAccess } from "../../../../../models/TicketAccess";

const HtmlTooltip = styled(({ className, ...props }: any) => <Tooltip {...props} classes={{ popper: className }} />)(
  ({ theme }) => ({
    [`& .MuiTooltip-tooltip`]: {
      backgroundColor: "#f5f5f9",
      color: "rgba(0, 0, 0, 0.87)",
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: "1px solid #dadde9",
    },
  })
);

interface IProps {
  ticket: Assort;
  currentQuantity: number;
  effectiveMax: number;
  onQuantityChange: (ticket: Assort, newValue: number) => void;
  onIncrement: (ticket: Assort, ticketAccess: TicketAccess) => void;
  onDecrement: (ticket: Assort, ticketAccess: TicketAccess) => void;
  ticketAccess: TicketAccess;
  openTooltip: string | null;
  setOpenTooltip: (value: string | null) => void;
  onIncrementDisabledClick: () => void;
}

const TicketCard: React.FC<IProps> = ({
  ticket,
  currentQuantity,
  effectiveMax,
  onQuantityChange,
  onIncrement,
  onDecrement,
  ticketAccess,
  openTooltip,
  setOpenTooltip,
  onIncrementDisabledClick,
}) => {
  const { t } = useTranslation();
  const isDesktop = useDesktopSize();
  const isDiscount = ticket.price !== ticket.listPrice;

  return (
    <div className="ticket-card">
      <div className="ticket-card__header">
        <div className="ticket-card__info">
          <div className="ticket-card__title">
            <HiOutlineTicket className="ticket-card__icon" />
            <span className="ticket-card__title-text">
              {ticket.displayWebName || ticket.name}
              {ticket.description.length > 2 && (
                <>
                  {isDesktop ? (
                    <HtmlTooltip
                      placement="right"
                      arrow={true}
                      title={
                        <article
                          className="tooltip-description"
                          dangerouslySetInnerHTML={{ __html: ticket.description }}
                        />
                      }
                    >
                      <span
                        className="ticket-card__tooltip-trigger"
                        onClick={() => setOpenTooltip(openTooltip === ticket.name ? null : ticket.name)}
                      >
                        <BiInfoCircle className="ticket-card__tooltip-icon" />
                      </span>
                    </HtmlTooltip>
                  ) : (
                    <BiInfoCircle
                      className="ticket-card__tooltip-icon"
                      onClick={() => setOpenTooltip(openTooltip === ticket.name ? null : ticket.name)}
                    />
                  )}
                </>
              )}
            </span>
          </div>
          <div className="ticket-card__price">
            {isDiscount && (
              <span className="ticket-card__price--old">
                {ticket.listPrice.toFixed(2)} {t("zl")}
              </span>
            )}
            <span className="ticket-card__price--current">
              {ticket.price.toFixed(2)} {t("zl")}
            </span>
          </div>
        </div>
      </div>

      <div className="ticket-card__quantity">
        <EAQuantitySelector
          value={currentQuantity}
          onChange={(newValue) => onQuantityChange(ticket, newValue)}
          onIncrement={() => onIncrement(ticket, ticketAccess)}
          onDecrement={() => onDecrement(ticket, ticketAccess)}
          min={0}
          max={effectiveMax}
          incrementDisabled={currentQuantity >= effectiveMax}
          decrementDisabled={currentQuantity <= 0}
          className="ticket-card__selector"
          onIncrementDisabledClick={onIncrementDisabledClick}
          showMaxInfo={true}
        />
      </div>
    </div>
  );
};

export default TicketCard;
